import React, {
  FC,
  MutableRefObject,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { throttle } from 'lodash';

import { ProcessedBlock, NullableHTMLDivElement, Present } from '/types';
import { PresentModeContext } from '/utils/contexts';
import { mod } from '/utils/functions';

const PRESENTATION_STEP_THROTTLE_WAIT = 250; // milliseconds

const notAnimated = [
  'block-asset',
  'block-hero',
  'block-section-header',
  'block-sub-section-header',
  'block-full-image',
];

const alwaysAnimated = ['block-product-wall-analyst'];

type IsBlockAnimated = (
  props: ProcessedBlock,
  presentMode: Present,
) => {
  animationEnabled: boolean;
  chartAnimationEnabled: boolean;
};

export const isBlockAnimated: IsBlockAnimated = (props, presentMode) => {
  const animationEnabled =
    (!!presentMode && alwaysAnimated.includes(props.type)) ||
    (presentMode === Present.Keynote &&
      !notAnimated.includes(props.type) &&
      !!props?.presentation_options?.animation?.enabled);

  const chartAnimationEnabled = Object.values(props?.chart_specs || []).some(
    (spec) => (spec.options?.highlight || []).length > 0,
  );
  return { animationEnabled, chartAnimationEnabled };
};

type StepPresentationArgs = {
  reverse: boolean;
  readyToSlide: boolean;
  index: number;
  numBlocks: number;
  ref: RefObject<NullableHTMLDivElement[]>;
  onScreenBlock: MutableRefObject<string | undefined>;
  setAnimationStep: React.Dispatch<React.SetStateAction<number>>;
};

const stepPresentation = throttle(
  ({
    reverse,
    readyToSlide,
    index,
    numBlocks,
    ref,
    onScreenBlock,
    setAnimationStep,
  }: StepPresentationArgs): void => {
    let blockToFocus = null;
    if (reverse || readyToSlide) {
      const nextBlockIdx = mod(reverse ? index - 1 : index + 1, numBlocks);
      blockToFocus = ref?.current?.[nextBlockIdx];
      blockToFocus?.scrollIntoView({ behavior: 'smooth' });
      const blockToFocusId = blockToFocus?.querySelector('section')?.id;
      if (blockToFocusId) {
        onScreenBlock.current = blockToFocusId;
      }
      // Reset the current block
      setAnimationStep(0);
    } else {
      setAnimationStep((x) => x + 1);
      blockToFocus = ref?.current?.[index];
    }
    blockToFocus?.focus({ preventScroll: true });
  },
  PRESENTATION_STEP_THROTTLE_WAIT,
);

type UsePresentControls = (props: {
  ref: RefObject<NullableHTMLDivElement[]>;
  index: number;
  animationEnabled?: boolean;
  chartAnimationEnabled?: boolean;
  numBlocks: number;
  onScreenBlock: MutableRefObject<string | undefined>;
}) => {
  PresentControls: React.FunctionComponent<{ children?: React.ReactNode }>;
  animationStep: number;
  setAnimationComplete: (state: boolean) => void;
};

const usePresentControls: UsePresentControls = ({
  ref,
  index,
  animationEnabled = false,
  chartAnimationEnabled = false,
  numBlocks,
  onScreenBlock,
}) => {
  const presentMode = useContext(PresentModeContext);
  const [animationStep, setAnimationStep] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    const stepPresentationArgs = {
      reverse: false,
      index,
      numBlocks,
      onScreenBlock,
      setAnimationStep,
      ref,
      readyToSlide:
        animationComplete || (!animationEnabled && !chartAnimationEnabled),
    };

    const onWheel = (event: WheelEvent): void => {
      if (presentMode) {
        event.preventDefault();
        stepPresentation({
          ...stepPresentationArgs,
          reverse: event.deltaY < 0,
        });
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (presentMode) {
        event.preventDefault();
        if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
          stepPresentation({ ...stepPresentationArgs, reverse: true });
        }
        if (['ArrowRight', 'ArrowDown', 'PageDown'].includes(event.key)) {
          stepPresentation(stepPresentationArgs);
        }
      }
    };

    const onClick = (): void => {
      if (presentMode) {
        stepPresentation(stepPresentationArgs);
      }
    };

    const block = ref?.current?.[index];
    const options: AddEventListenerOptions = { passive: false };
    if (presentMode) {
      block?.addEventListener('wheel', onWheel, options);
      block?.addEventListener('keydown', onKeyDown);
      block?.addEventListener('click', onClick);
    }
    return (): void => {
      block?.removeEventListener('wheel', onWheel, options);
      block?.removeEventListener('keydown', onKeyDown);
      block?.removeEventListener('click', onClick);
    };
  }, [
    animationComplete,
    animationEnabled,
    chartAnimationEnabled,
    index,
    numBlocks,
    onScreenBlock,
    presentMode,
    ref,
  ]);

  const PresentControls: FC<{ children?: React.ReactNode }> = useCallback(
    ({ children }) => (
      <div
        tabIndex={-1}
        ref={(el): void => {
          if (ref && ref.current) {
            // eslint-disable-next-line react-compiler/react-compiler
            ref.current[index] = el;
          }
        }}
      >
        {children}
      </div>
    ),
    [ref, index],
  );

  return { PresentControls, setAnimationComplete, animationStep };
};

export default usePresentControls;
